import { Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { Actions, Effect } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';
import { HttpErrorResponse } from '@angular/common/http';

import { ModuleConfiguration } from '../customer.module.config';

import * as dashboardActions from '../../pillar/dashboard/store/actions';
import * as workspaceActions from '../../pillar/workspace/store/actions';
import * as securityActions from '../../security/store/actions';
import * as userActions from '../store/actions';
import { UserService } from './UserService';
import { UserListModel } from '../models/UserListModel';
import { AddUserModel } from '../models/AddUserModel';
import { QueryResponse } from '../../core/services/QueryResponse';
import { UserModel } from '../models/UserModel';
import { UserAccountDetailedStatusModel } from '../models/UserAccountDetailedStatusModel';
import { ModelResponse } from '../../core/services/ModelResponse';
import { ResponseMessage } from '../../core/services/ResponseMessage';
import { ResponseMessageType } from '../../core/services/ResponseMessageType';
import { LoadPagePayload } from '../../pillar/common/actions/LoadPagePayload';
import { LoadPageAction } from '../../pillar/common/actions/LoadPageAction';
import { UserGroupModel } from '../models/UserGroupModel';
import { LookupModel } from '../../common/models/LookupModel';
import { ResetPasswordAction } from '../store/actions';
import { RoleModel } from '../../security/models/RoleModel';
import { ActionError } from '../../pillar/common/actions/ActionError';
import { UserNameModel } from '../models/UserNameModel';
import { NotificationModel } from '../models/NotificationModel';
import { ManageRoleErrorAction } from '../../security/store/actions';
import { ManageUserGroupErrorAction } from '../store/actions/ManageUserGroupErrorAction';

@Injectable()
export class UserServiceEffects {
  @Effect()
  getUserList$: Observable<Action> = this.actions$.ofType(dashboardActions.getQueryListType<UserListModel>(ModuleConfiguration.moduleName, UserListModel))
      .map((a: dashboardActions.QueryListAction<UserListModel>) => a.payload)
      .switchMap((payload: LoadPagePayload) => this.userService.getUserList(payload.page, payload.pageSize, payload.sort, payload.sortDirection, payload.searchTerm)
        .catch((error: HttpErrorResponse) => Observable.of(new QueryResponse<UserListModel>(error)))
      .map((response: QueryResponse<UserListModel>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, UserListModel, { errors: response.messages.map(m => m.value) });
        }
        return new dashboardActions.QuerySuccessAction(ModuleConfiguration.moduleName, UserListModel, { items: response.items, totalCount: response.totalCount });
      }));
  @Effect()
  getGroupLookups$: Observable<Action> = this.actions$.ofType(dashboardActions.getQueryListType<UserGroupModel>(ModuleConfiguration.moduleName, UserGroupModel))
    .map((a: dashboardActions.QueryListAction<UserGroupModel>) => a.payload)
      .switchMap((payload: LoadPagePayload) => this.userService.getGroupLookupsList(payload.page, payload.pageSize, payload.sort, payload.sortDirection, payload.searchTerm)
        .catch((error: HttpErrorResponse) => Observable.of(new QueryResponse<any>(error)))
      .map((response: QueryResponse<any>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, UserListModel, { errors: response.messages.map(m => m.value) });
        }
        return new dashboardActions.QuerySuccessAction(ModuleConfiguration.moduleName, UserGroupModel, { items: response.items, totalCount: response.totalCount });
      }));
  @Effect()
  getAvailableRoles$: Observable<Action> = this.actions$.ofType(securityActions.QUERY_AVAILABLE_ROLES)
      .switchMap((action: LoadPageAction) => this.userService.getAvailableRoles(action.payload.page, action.payload.pageSize, action.payload.sort, action.payload.sortDirection, action.payload.searchTerm)
        .catch((error: HttpErrorResponse) => Observable.of(new QueryResponse<any>(error)))
      .map((response: QueryResponse<any>) => {
        if (response.hasError) {
          return new ActionError(action, response.messages.map(m => m.value));
        }
        return new securityActions.QueryAvailableRolesSuccessAction({ items: response.items });
      }));
  @Effect()
  getCurrentUserSubmittingEntities$: Observable<Action> = this.actions$.ofType(securityActions.GET_CURRENT_USER_SUBMITTING_ENTITIES)
      .switchMap((action: securityActions.GetCurrentUserSubmittingEntitiesAction) => this.userService.getCurrentUserSubmittingEntities()
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<LookupModel[]>(error)))
      .map((response: ModelResponse<LookupModel[]>) => {
        if (response.hasError) {
          return new ActionError(action, response.messages.map(m => m.value));
        }
        return new securityActions.GetCurrentUserSubmittingEntitiesSuccessAction({ submittingEntities: response.model });
      }));
  @Effect()
  getUserAssociatedSubmittingEntities$: Observable<Action> = this.actions$.ofType(userActions.GET_USER_SUBMITTING_ENTITIES)
    .switchMap((action: userActions.GetUserSubmittingEntitiesAction) => this.userService.getUserAssociatedSubmittingEntities(action.payload.userKey)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<LookupModel[]>(error)))
      .map((response: ModelResponse<LookupModel[]>) => {
        if (response.hasError) {
          return new ActionError(action, response.messages.map(m => m.value));
        }
        return new userActions.GetUserSubmittingEntitiesSuccessAction({ userKey: action.payload.userKey, submittingEntities: response.model });
      }));
  @Effect()
  getCurrentStatus$: Observable<Action> = this.actions$.ofType(userActions.GET_USER_CURRENT_STATUS)
      .switchMap((action: userActions.GetUserCurrentStatusAction) => this.userService.GetUserCurrentStatus(action.payload.userKey)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<UserAccountDetailedStatusModel>(error)))
        .map((response: ModelResponse<UserAccountDetailedStatusModel>) => {
        if (response.hasError) {
          return new ActionError(action, response.messages.map(m => m.value));
        }
          return new userActions.GetUserCurrentStatusSuccessAction({ userKey: action.payload.userKey, userCurrentStatus: response.model });
      }));
  @Effect()
  getUser$: Observable<Action> = this.actions$.ofType(dashboardActions.getSelectionType<UserModel>(ModuleConfiguration.moduleName, UserModel))
      .switchMap((a: dashboardActions.GetSelectionAction<UserModel>) => this.userService.getUser(a.payload.key)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<UserModel>(error)))
      .map((response: ModelResponse<UserModel>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, UserListModel, { errors: response.messages.map(m => m.value) });
        }
        return new dashboardActions.GetSelectionSuccessAction(ModuleConfiguration.moduleName, UserModel, { model: response.model });
      }));
  @Effect()
  addUser$: Observable<Action> = this.actions$.ofType(dashboardActions.addType<AddUserModel>(ModuleConfiguration.moduleName, AddUserModel))
      .switchMap((action: dashboardActions.AddAction<AddUserModel>) => this.userService.addUser(action.payload.model)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<string>(error)))
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, UserListModel, { errors: response.messages.map(m => m.value) });
        }
        return new dashboardActions.AddSuccessAction(ModuleConfiguration.moduleName, AddUserModel, { key: response.model });
      }));
  @Effect()
  resetPassword$: Observable<Action> = this.actions$.ofType(userActions.RESET_PASSWORD)
      .switchMap((a: ResetPasswordAction) => this.userService.resetPassword(a.payload.user.key, a.payload.user.identifier)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<boolean>(error)))
        .map((response: ModelResponse<boolean>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, UserListModel, { errors: response.messages.map(m => m.value) });
          }
          return new userActions.ResetPasswordSuccessAction(a.payload);
      }));
  @Effect()
  lockAccount: Observable<Action> = this.actions$.ofType(userActions.LOCK_ACCOUNT)
      .switchMap((a: userActions.LockAccountAction) => this.userService.lockAccount(a.payload.user.key, a.payload.user.reason)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<string>(error)))
        .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, UserListModel, { errors: response.messages.map(m => m.value) });
        }
          return new userActions.LockAccountSuccessAction({ user: a.payload.user, status: response.model });
      }));
  @Effect()
  disableAccount: Observable<Action> = this.actions$.ofType(userActions.DISABLE_ACCOUNT)
    .switchMap((a: userActions.DisableAccountAction) => this.userService.disableAccount(a.payload.user.key)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<string>(error)))
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, UserListModel, { errors: response.messages.map(m => m.value) });
        }
        return new userActions.DisableAccountSuccessAction({ user: a.payload.user, status: response.model });
      }));
  @Effect()
  unlockAccount: Observable<Action> = this.actions$.ofType(userActions.UNLOCK_ACCOUNT)
      .switchMap((a: userActions.UnlockAccountAction) => this.userService.unlockAccount(a.payload.user.key)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<string>(error)))
        .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, UserListModel, { errors: response.messages.map(m => m.value) });
        }
          return new userActions.UnlockAccountSuccessAction({ user: a.payload.user, status: response.model });
      }));
  @Effect()
  enableAccount: Observable<Action> = this.actions$.ofType(userActions.ENABLE_ACCOUNT)
    .switchMap((a: userActions.EnableAccountAction) => this.userService.enableAccount(a.payload.user.key)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<string>(error)))
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, UserListModel, { errors: response.messages.map(m => m.value) });
        }
        return new userActions.EnableAccountSuccessAction({ user: a.payload.user, status: response.model });
      }));
  @Effect()
  addGroup$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<UserModel, LookupModel>(ModuleConfiguration.moduleName, UserModel, 'groups'))
      .switchMap((a: workspaceActions.UpdateModelAction<UserModel, LookupModel>) => this.userService.addGroup(a.payload.key, a.payload.model)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<LookupModel>(error)))
        .map((response: ModelResponse<LookupModel>) => {
          if (response.hasError) {
            return new workspaceActions.UpdateModelErrorAction(
              {
                action: a,
                errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
              });
        }
          return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
            UserModel,
            'groups',
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));  
  @Effect()
      addAllGroups$: Observable<Action> = this.actions$.ofType(userActions.ADD_ALL_USER_GROUPS)
      .switchMap((action: userActions.AddAllGroupsToUserAction) => this.userService.addAllGroups(action.payload.userKey, action.payload.allowAllGroups,action.payload.lookUpModels)
          .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<LookupModel>(error)))
          .map((response: ModelResponse<LookupModel[]>) => {
            if (response.hasError) {
              return new ManageUserGroupErrorAction(action, response.messages.map(m => m.value));
            }
            return new userActions.AddAllGroupsToUserSuccessAction({ userKey: action.payload.userKey, userGroups: response.model });
          }));  

  @Effect()
  removeGroup$: Observable<Action> = this.actions$.ofType(workspaceActions.removeModelType<UserModel, LookupModel>(ModuleConfiguration.moduleName, UserModel, 'groups'))
      .switchMap((a: workspaceActions.RemoveModelAction<UserModel, LookupModel>) => this.userService.removeGroup(a.payload.key, a.payload.model )
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<LookupModel>(error)))
      .map((response: ModelResponse<LookupModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.RemoveModelSuccessAction(ModuleConfiguration.moduleName,
          UserModel,
          'groups',
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  addRole$: Observable<Action> = this.actions$.ofType(userActions.ADD_ROLE_TO_USER)
      .switchMap((action: userActions.AddRoleToUserAction) => this.userService.addRole(action.payload.userKey, action.payload.role)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<RoleModel>(error)))
      .map((response: ModelResponse<RoleModel>) => {
        if (response.hasError) {
          return new ManageRoleErrorAction(action, response.messages.map(m => m.value));
        }
        return new userActions.AddRoleToUserSuccessAction({ userKey: action.payload.userKey, role: response.model });
      }));
  @Effect()
  removeRole$: Observable<Action> = this.actions$.ofType(userActions.REMOVE_ROLE_FROM_USER)
      .switchMap((action: userActions.RemoveRoleFromUserAction) => this.userService.removeRole(action.payload.userKey, action.payload.role.key)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<boolean>(error)))
      .map((response: ModelResponse<boolean>) => {
        if (response.hasError) {
          return new ManageRoleErrorAction(action, response.messages.map(m => m.value));
        }
        return new userActions.RemoveRoleFromUserSuccessAction({ userKey: action.payload.userKey, role: action.payload.role });
      }));
  @Effect()
  reviseName$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType(ModuleConfiguration.moduleName, UserModel, UserNameModel))
    .switchMap((action: workspaceActions.UpdateModelAction<UserModel, UserNameModel>) => this.userService.reviseName(action.payload.key, action.payload.model)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<UserNameModel>(error)))
      .map((response: ModelResponse<UserNameModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: action,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName, UserModel, UserNameModel, { key: action.payload.key, model: response.model, control: action.payload.control });
      }));
  @Effect()
  reviseEmailAddress$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType(ModuleConfiguration.moduleName, UserModel, 'emailAddress'))
      .switchMap((action: workspaceActions.UpdateModelAction<UserModel, string>) => this.userService.reviseEmailAddress(action.payload.key, action.payload.model)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<string>(error)))
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: action,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName, UserModel, 'emailAddress', { key: action.payload.key, model: response.model, control: action.payload.control });
      }));
  @Effect()
  addNotification$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<UserModel, NotificationModel>(ModuleConfiguration.moduleName, UserModel, NotificationModel))
      .switchMap((a: workspaceActions.UpdateModelAction<UserModel, NotificationModel>) => this.userService.addNotification(a.payload.key, a.payload.model)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<NotificationModel>(error)))
        .map((response: ModelResponse<NotificationModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          UserModel,
          NotificationModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  removeNotification$: Observable<Action> = this.actions$.ofType(workspaceActions.removeModelType<UserModel, NotificationModel>(ModuleConfiguration.moduleName, UserModel, NotificationModel))
      .switchMap((a: workspaceActions.RemoveModelAction<UserModel, NotificationModel>) => this.userService.removeNotification(a.payload.key, a.payload.model)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<NotificationModel>(error)))
        .map((response: ModelResponse<NotificationModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.RemoveModelSuccessAction(ModuleConfiguration.moduleName,
          UserModel,
          NotificationModel,
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  @Effect()
  getNotificationTypes$: Observable<Action> = this.actions$.ofType(userActions.GET_NOTIFICATION_TYPES)
    .switchMap((action: userActions.GetNotificationTypesAction) => this.userService.getNotificationTypes()
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<LookupModel[]>(error)))
      .map((response: ModelResponse<LookupModel[]>) => {
        if (response.hasError) {
          return new ActionError(action, response.messages.map(m => m.value));
        }
        return new userActions.GetNotificationTypesSuccessAction({ notificationTypes: response.model });
      }));
  @Effect()
  getCurrentUserProfile$: Observable<Action> = this.actions$.ofType(securityActions.GET_CURRENT_USER)
      .switchMap((action: userActions.GetNotificationTypesAction) => this.userService.getCurrentUser()
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<UserModel>(error)))
        .map((response: ModelResponse<UserModel>) => {
        if (response.hasError) {
          return new ActionError(action, response.messages.map(m => m.value));
          }
          return new securityActions.GetCurrentUserProfileSuccessAction(response.model);
      }));
 @Effect()
 globalLock$: Observable<Action> = this.actions$.ofType(userActions.GLOBAL_LOCK)
     .switchMap((a: userActions.GlobalLockAction) => this.userService.globalLock()
       .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<string>(error)))
       .map((response: ModelResponse<string>) => {
       if (response.hasError) {
         return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, UserListModel, { errors: response.messages.map(m => m.value) });
       }
         return new userActions.GlobalLockSuccessAction();
     }));
 @Effect()
 globalUnlock$: Observable<Action> = this.actions$.ofType(userActions.GLOBAL_UNLOCK)
     .switchMap((a: userActions.GlobalUnLockAction) => this.userService.globalUnlock()
       .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<string>(error)))
       .map((response: ModelResponse<string>) => {
       if (response.hasError) {
         return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, UserListModel, { errors: response.messages.map(m => m.value) });
       }
         return new userActions.GlobalUnlockSuccessAction();
     }));
  @Effect()
  checkGlobalLock$: Observable<Action> = this.actions$.ofType(userActions.CHECK_GLOBAL_LOCK)
      .switchMap((a: userActions.CheckGlobalLockAction) => this.userService.checkGlobalLock()
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<boolean>(error)))
        .map((response: ModelResponse<boolean>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, UserListModel, { errors: response.messages.map(m => m.value) });
        }
          return new userActions.CheckGlobalLockSuccessAction( { active: response.model });
      }));

  constructor(
    private userService: UserService,
    private actions$: Actions
  ) {}
}



// WEBPACK FOOTER //
// ./src/app/customer/services/UserService.effects.ts